import React, { useState } from "react";
import "./whatsAppMessageModal.scss";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { FrontEndURL, whatsAppMSGS } from "../../Constants/Constants";

const WhatsAppMessageModal = ({ item, onClose, isCurrentGuest = false }) => {
  const [selectedIDs, setSelectedIDs] = useState(
    isCurrentGuest ? [] : ["default-link"],
  ); // default-link is checked by default if not current guest
  const [phoneError, setPhoneError] = useState("");

  const formatPhoneNumber = (rawNumber) => {
    if (!rawNumber) return "";
    let digitsOnly = rawNumber.replace(/\D/g, "");
    if (digitsOnly.startsWith("0")) {
      digitsOnly = "94" + digitsOnly.substring(1);
    }
    return digitsOnly;
  };

  const contactNumber = formatPhoneNumber(
    item?.Contact_Number || item?.ContactNumber,
  );
  const customerLink =
    FrontEndURL +
    `addreference?refId=${item?.Refference_Number || item?.Booking_Reference_ID}`;

  const getFormattedMessage = (msg) => {
    if (typeof msg === "string") {
      // Check if it's a media asset path (e.g. starting with /static/media/ or having asset extensions)
      if (
        msg.includes("/static/media/") ||
        msg.endsWith(".jpeg") ||
        msg.endsWith(".jpg") ||
        msg.endsWith(".png") ||
        msg.endsWith(".mp4")
      ) {
        return `${window.location.origin}${msg}`;
      }
      return msg
        .replace("by email; [EMAIL_ADDRESS]", `here:\n \n${customerLink}`)
        .replace(
          "by email; reception@cinnamonlakehouse.it",
          `here:\n \n${customerLink}`,
        );
    }
    return "";
  };

  // Build the list of all options
  const filteredMsgs = isCurrentGuest
    ? whatsAppMSGS.filter((m) => m.ID >= 2 && m.ID <= 5)
    : whatsAppMSGS;

  const options = [
    ...(!isCurrentGuest
      ? [
          {
            id: "default-link",
            name: "Online Check-In Link",
            msg: ` Hi, welcome to Cinnamon Lake House... \n please fill out your reference details here:\n \n${customerLink}`,
          },
        ]
      : []),
    ...filteredMsgs.map((msgItem) => {
      let formattedMsg = getFormattedMessage(msgItem.msg);
      if (msgItem.ID === 5) {
        formattedMsg = `This is your Wifi access QR: ${formattedMsg}`;
      } else if (msgItem.ID === 6) {
        formattedMsg = `This is your door lock code: ${formattedMsg}`;
      }
      return {
        id: msgItem.ID.toString(),
        name: msgItem.name,
        msg: formattedMsg,
      };
    }),
    {
      id: "direct-chat",
      name: "Direct Chat (No Message)",
      msg: "",
    },
  ];

  // Handle checking/unchecking
  const handleCheckboxChange = (id) => {
    if (id === "direct-chat") {
      // If direct-chat is checked, clear all other selections
      if (selectedIDs.includes("direct-chat")) {
        setSelectedIDs([]);
      } else {
        setSelectedIDs(["direct-chat"]);
      }
    } else {
      // If any other option is checked, make sure direct-chat is removed
      let updated = [...selectedIDs].filter((x) => x !== "direct-chat");
      if (updated.includes(id)) {
        updated = updated.filter((x) => x !== id);
      } else {
        updated.push(id);
      }
      setSelectedIDs(updated);
    }
  };

  // Build the final combined preview message
  const getCombinedMessage = () => {
    if (selectedIDs.includes("direct-chat") || selectedIDs.length === 0) {
      return "";
    }
    // Sort selected options to maintain their order in the options list
    const selectedOptions = options.filter(
      (opt) => selectedIDs.includes(opt.id) && opt.id !== "direct-chat",
    );
    return selectedOptions.map((opt) => opt.msg).join("\n\n");
  };

  const handleSend = () => {
    if (!contactNumber) {
      setPhoneError("Phone number is missing or invalid");
      return;
    }

    const finalMessage = getCombinedMessage();
    const waUrl = finalMessage
      ? `https://api.whatsapp.com/send?phone=${contactNumber}&text=${encodeURIComponent(finalMessage)}`
      : `https://api.whatsapp.com/send?phone=${contactNumber}`;

    window.open(waUrl, "_blank");
    onClose();
  };

  return (
    <div className="wa-modal-backdrop">
      <div className="wa-modal-container">
        <div className="wa-modal-header">
          <div className="wa-header-title">
            <WhatsAppIcon className="wa-icon" />
            <span>Send WhatsApp Message</span>
          </div>
          <button className="wa-close-btn" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="wa-modal-body">
          <div className="wa-guest-info">
            <p>
              <strong>Guest:</strong> {item?.Customer_Fname || item?.Fname}{" "}
              {item?.Customer_Lname || item?.Lname}
            </p>
            <p>
              <strong>Phone:</strong>{" "}
              {item?.Contact_Number || item?.ContactNumber || "N/A"}
            </p>
          </div>

          <div className="wa-media-attachments-tip">
            <span className="wa-tip-title">💡 Media Attachment Tip:</span>
            <span className="wa-tip-desc">
              WhatsApp Web links can only pre-fill text. Selecting{" "}
              {isCurrentGuest ? (
                <strong>Wi-Fi</strong>
              ) : (
                <>
                  <strong>Wi-Fi</strong> or <strong>Security Code</strong>
                </>
              )}{" "}
              will send a clickable URL to the guest. To send them as direct
              file attachments, open them below to save and drag-and-drop them:
            </span>
            <div className="wa-media-buttons">
              <a
                href={getFormattedMessage(
                  whatsAppMSGS.find((m) => m.ID === 5)?.msg,
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="wa-media-btn"
              >
                🖼️ Open Wi-Fi QR Code
              </a>
              {!isCurrentGuest && (
                <a
                  href={getFormattedMessage(
                    whatsAppMSGS.find((m) => m.ID === 6)?.msg,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wa-media-btn"
                >
                  🎥 Open Security Video
                </a>
              )}
            </div>
          </div>

          <div className="wa-options-title">Select messages to include:</div>
          <div className="wa-options-list">
            {options.map((opt) => {
              const isChecked = selectedIDs.includes(opt.id);
              return (
                <label
                  key={opt.id}
                  className={`wa-option-item ${isChecked ? "active" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleCheckboxChange(opt.id)}
                  />
                  <div className="wa-option-details">
                    <span className="wa-option-name">{opt.name}</span>
                    {opt.msg && (
                      <span className="wa-option-preview">
                        {opt.msg.length > 80
                          ? `${opt.msg.slice(0, 80)}...`
                          : opt.msg}
                      </span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>

          {selectedIDs.length > 0 && !selectedIDs.includes("direct-chat") && (
            <div className="wa-preview-section">
              <div className="wa-preview-title">Message Preview:</div>
              <div className="wa-preview-box">{getCombinedMessage()}</div>
            </div>
          )}

          {selectedIDs.length === 0 && (
            <div className="wa-error-msg">
              ⚠️ Please select at least one message template or choose Direct
              Chat to proceed.
            </div>
          )}
          {phoneError && <div className="wa-error-msg">{phoneError}</div>}
        </div>

        <div className="wa-modal-footer">
          <button className="wa-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="wa-btn-send"
            onClick={handleSend}
            disabled={selectedIDs.length === 0}
          >
            <WhatsAppIcon className="wa-btn-icon" />
            {selectedIDs.includes("direct-chat")
              ? "Open Direct Chat"
              : "Send Selected"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppMessageModal;
